import { useEffect, useState } from 'react';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { captureException } from '../../../lib/telemetry/posthog/frontend';

/** Result of resolving whether the current session can view a conversation. */
export interface AuthorizedConversationViewer {
  authorizedConversationId: string | null;
  authorizedUnavailableReason: 'insufficient_credits' | null;
  isResolving: boolean;
}

/** Resolve an RLS-authorized admin destination for a public conversation room. */
export function useAuthorizedConversationViewer(
  roomId: string,
  interviewDataId?: string,
  resolveUnavailableReason = false,
): AuthorizedConversationViewer {
  const { session, loading, supabase } = useSupabase();
  const userId = session?.user.id ?? null;
  const normalizedRoomId = roomId.trim().toUpperCase();
  const [result, setResult] = useState<AuthorizedConversationViewer>({
    authorizedConversationId: null,
    authorizedUnavailableReason: null,
    isResolving: loading || Boolean(userId && normalizedRoomId && resolveUnavailableReason),
  });

  useEffect(() => {
    if (loading) {
      setResult({
        authorizedConversationId: null,
        authorizedUnavailableReason: null,
        isResolving: true,
      });
      return undefined;
    }

    if (!userId || !normalizedRoomId || !resolveUnavailableReason) {
      setResult({
        authorizedConversationId: null,
        authorizedUnavailableReason: null,
        isResolving: false,
      });
      return undefined;
    }

    let active = true;
    setResult({
      authorizedConversationId: null,
      authorizedUnavailableReason: null,
      isResolving: true,
    });

    void (async () => {
      const sessionResult = await supabase
        .from('interview_sessions')
        .select('id, organization_id')
        .eq('room_id', normalizedRoomId)
        .maybeSingle();
      if (!active) return;

      if (sessionResult.error) {
        captureException(sessionResult.error, 'useAuthorizedConversationViewer.resolve', {
          roomId: normalizedRoomId,
        });
        setResult({
          authorizedConversationId: null,
          authorizedUnavailableReason: null,
          isResolving: false,
        });
        return;
      }

      const authorizedConversationId = sessionResult.data?.id ?? null;
      const organizationId = sessionResult.data?.organization_id ?? null;
      let canViewBilling = false;
      if (organizationId) {
        const permissionResult = await supabase.rpc('org_has_permission', {
          p_org_id: organizationId,
          p_permission: 'organization:view_billing',
        });
        if (!active) return;
        if (permissionResult.error) {
          captureException(
            permissionResult.error,
            'useAuthorizedConversationViewer.resolveBillingPermission',
            { roomId: normalizedRoomId },
          );
        } else {
          canViewBilling = permissionResult.data === true;
        }
      }
      let authorizedUnavailableReason: 'insufficient_credits' | null = null;

      if (canViewBilling && interviewDataId && authorizedConversationId) {
        const responseResult = await supabase
          .from('interview_data')
          .select('response_status')
          .eq('id', interviewDataId)
          .eq('interview_session_id', authorizedConversationId)
          .maybeSingle();
        if (!active) return;
        if (responseResult.error) {
          captureException(
            responseResult.error,
            'useAuthorizedConversationViewer.resolveResponseStatus',
            { roomId: normalizedRoomId, interviewDataId },
          );
        } else if (responseResult.data?.response_status === 'insufficient') {
          authorizedUnavailableReason = 'insufficient_credits';
        }
      }

      setResult({
        authorizedConversationId,
        authorizedUnavailableReason,
        isResolving: false,
      });
    })();

    return () => {
      active = false;
    };
  }, [interviewDataId, loading, normalizedRoomId, resolveUnavailableReason, supabase, userId]);

  return result;
}
